import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeftIcon, DownloadIcon, SearchIcon, XIcon, DocumentTextIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { DashHeader, Footer, LoadingOverlay, Signin } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { formatTitle } from '../utils/formatTitle';
import NoAccess from './NoAccess';

// FEAT-APPLY phase 5 (APP-003): one job's applicants. Filters by source board and status,
// searches name / email / phone, downloads résumés, updates status and exports CSV.
// All data comes from the employerApplications function, which scopes everything to the caller.

const BOARD_LABELS = {
  NewJobLeads: 'New',
  YouthJobLeads: 'Youth',
  AbleJobLeads: 'Able',
  RefugeeJobLeads: 'Refugee',
  AboriginalJobLeads: 'Aboriginal',
  CanadaJobLeads: 'CanadaJobLeads.ca', // the national /jobs search (GROWTH-004)
};
const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected', label: 'Rejected' },
];
const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  reviewed: 'bg-gray-100 text-gray-700 border-gray-200',
  shortlisted: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const api = (body) => axios.post('/.netlify/functions/employerApplications', body);

// Exact dates, never relative (D-013).
const isoDate = (value) => (value ? String(value).slice(0, 10) : '—');

// CSV cell: quoted, quotes doubled, and a leading =,+,-,@ neutralised so spreadsheets
// never treat applicant text as a formula.
function csvCell(value) {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ApplicantRow({ app, onStatus, onResume, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-900">{app.name}</p>
          <p className="mt-0.5 break-all text-sm text-gray-600">
            <a href={`mailto:${app.email}`} className="hover:text-primary hover:underline">
              {app.email}
            </a>
            {app.phone && <span className="text-gray-400"> · </span>}
            {app.phone && (
              <a href={`tel:${app.phone}`} className="hover:text-primary hover:underline">
                {app.phone}
              </a>
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Applied {isoDate(app.appliedAt)} via{' '}
            <span className="font-semibold text-gray-700">{app.source}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
          <select
            value={app.status}
            onChange={(e) => onStatus(app, e.target.value)}
            disabled={busy}
            aria-label={`Status for ${app.name}`}
            className={`cursor-pointer rounded-md border py-1 pl-2 pr-8 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary ${STATUS_STYLES[app.status] || STATUS_STYLES.new}`}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onResume(app)}
            disabled={busy}
            className="inline-flex items-center rounded-md border border-primary px-3 py-1 text-xs font-semibold text-primary hover:bg-red-50 disabled:opacity-50"
          >
            <DownloadIcon className="mr-1 h-4 w-4" aria-hidden="true" />
            Résumé
          </button>
        </div>
      </div>
      {app.coverLetter ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="inline-flex items-center text-xs font-semibold text-gray-600 hover:text-primary"
            aria-expanded={open}
          >
            <DocumentTextIcon className="mr-1 h-4 w-4" aria-hidden="true" />
            Cover letter
            {open ? <ChevronUpIcon className="ml-0.5 h-4 w-4" /> : <ChevronDownIcon className="ml-0.5 h-4 w-4" />}
          </button>
          {open && (
            <p className="mt-2 whitespace-pre-line border-l-2 border-gray-200 pl-3 text-sm text-gray-700">{app.coverLetter}</p>
          )}
        </div>
      ) : (
        <p className="mt-3 text-xs text-gray-400">No cover letter</p>
      )}
    </li>
  );
}

function Applications() {
  const { jobId } = useParams();
  const { currentUser, signout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [board, setBoard] = useState('all');
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    api({ action: 'list', jobId })
      .then((res) => {
        setJob(res.data.job);
        setApps(res.data.applications || []);
      })
      .catch((err) => {
        if (err.response && [403, 404].includes(err.response.status)) setForbidden(true);
        else setError('We could not load the applicants. Please refresh the page.');
      })
      .finally(() => setLoading(false));
  }, [currentUser, jobId]);

  const boardCounts = useMemo(() => {
    const counts = {};
    apps.forEach((a) => {
      counts[a.source] = (counts[a.source] || 0) + 1;
    });
    return counts;
  }, [apps]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    return apps.filter((a) => {
      if (board !== 'all' && a.source !== board) return false;
      if (status !== 'all' && a.status !== status) return false;
      if (!q) return true;
      return (
        String(a.name).toLowerCase().includes(q) ||
        String(a.email).toLowerCase().includes(q) ||
        (digits.length >= 3 && String(a.phone || '').replace(/\D/g, '').includes(digits))
      );
    });
  }, [apps, board, status, query]);

  function handleSignout() {
    try {
      signout();
      navigate('/');
    } catch (err) {
      console.log(err);
    }
  }

  async function changeStatus(app, next) {
    const prev = app.status;
    setBusyId(app._id);
    setApps((list) => list.map((a) => (a._id === app._id ? { ...a, status: next } : a)));
    try {
      await api({ action: 'status', appId: app._id, status: next });
    } catch (err) {
      setApps((list) => list.map((a) => (a._id === app._id ? { ...a, status: prev } : a)));
      setError('We could not update the status. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function downloadResume(app) {
    setBusyId(app._id);
    try {
      const res = await axios.post(
        '/.netlify/functions/employerApplications',
        { action: 'resume', appId: app._id },
        { responseType: 'blob' }
      );
      downloadBlob(res.data, app.resumeName || 'resume');
    } catch (err) {
      setError('We could not download that résumé. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    const header = ['Name', 'Email', 'Phone', 'Board', 'Applied', 'Status', 'Résumé file', 'Cover letter'];
    const rows = shown.map((a) => [a.name, a.email, a.phone, a.source, isoDate(a.appliedAt), a.status, a.resumeName, a.coverLetter]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
    const slug = String((job && job.title) || 'job').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
    downloadBlob(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), `applicants-${slug}-${isoDate(new Date().toISOString())}.csv`);
  }

  if (loading) return <LoadingOverlay />;
  if (!currentUser) return <Signin />;
  if (forbidden) return <NoAccess />;

  const title = job ? formatTitle(job.title) : 'Applicants';
  const boards = Object.keys(boardCounts).sort();
  const chip = (active) =>
    `rounded-full border px-3 py-1 text-xs font-semibold ${active ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-primary'}`;

  return (
    <div className="flex min-h-screen flex-col">
      <DashHeader handle={handleSignout} userEmail={currentUser?.email} />
      <main className="mx-auto w-full max-w-6xl flex-grow px-4 py-8 sm:px-6">
        <Link to="/dashboard" className="inline-flex items-center font-semibold text-gray-900">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          Back to dashboard
        </Link>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold font-[Poppins] text-gray-900">{title}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {apps.length} {apps.length === 1 ? 'applicant' : 'applicants'}
              {job && job.postExpiry ? ` · Posting expires ${isoDate(job.postExpiry)}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!shown.length}
            className="inline-flex items-center justify-center rounded-md border border-primary bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <DownloadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Download CSV
          </button>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {apps.length > 0 && (
          <div className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or phone"
                aria-label="Search applicants"
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-2 p-0.5 text-gray-400 hover:text-gray-600" aria-label="Clear search">
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-medium text-gray-500">Board</span>
              <button type="button" className={chip(board === 'all')} onClick={() => setBoard('all')}>
                All {apps.length}
              </button>
              {boards.map((b) => (
                <button key={b} type="button" className={chip(board === b)} onClick={() => setBoard(b)} title={b}>
                  {BOARD_LABELS[b] || b} {boardCounts[b]}
                </button>
              ))}
              <span className="ml-3 mr-1 text-xs font-medium text-gray-500">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filter by status"
                className="cursor-pointer rounded-md border border-gray-300 bg-white py-1 pl-2 pr-8 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All</option>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {apps.length === 0 ? (
          <div className="mt-8 rounded-lg border border-dashed border-gray-300 p-10 text-center">
            <p className="font-semibold text-gray-900">No applicants yet</p>
            <p className="mt-1 text-sm text-gray-600">
              When candidates apply on the job boards, they'll appear here and you'll get a copy by email.
            </p>
          </div>
        ) : shown.length === 0 ? (
          <p className="mt-8 text-center text-sm text-gray-600">No applicants match these filters.</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {shown.map((app) => (
              <ApplicantRow key={app._id} app={app} onStatus={changeStatus} onResume={downloadResume} busy={busyId === app._id} />
            ))}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Applications;
